import {
  CasNumberDTO,
  ConflictResolutionType,
  ExtendedIngredientDTO,
  IngredientAlias,
  IngredientConflictDto,
  IngredientImportConflictType,
  IngredientTypeDTO,
} from 'business/models/Models';
import { ValidateIngredient } from 'business/services/IngredientService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import RMTable from 'components/customComponents/Controls/RMTable';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxButton, FmlxModal } from 'fmlx-common-ui';
import { cloneDeep, isEqual } from 'lodash';
import React, { createContext, useEffect, useState } from 'react';
import { getNewId, showAlertSnackBar } from 'utils/Global';
import '../ingredients.scss';
import { IngredientsConflictsModelProps, KeyValuePairDTO } from '../IngredientsConstants';
import ConflictedIngredient from './ConflictedIngredient';
import { NewIngredient } from './NewIngredient';
import SubstituteIngredient from './SubstituteIngredient';

const newIngredientContext = createContext(null);
const substituteIngredientContext = createContext(null);
const IngredientsConflictsModel = (props: IngredientsConflictsModelProps) => {
  const {
    showModal,
    setShowModal,
    conflictIngredientsList,
    setConflictIngredientsList,
    vendors,
    resolvedIngredientsList,
    setResolvedIngredientsList,
    saveIngredients,
    isScreen,
    conditions,
    setConditions,
  } = props;
  const [values, setValues] = useState([]);
  const [conditionIngValues, setConditionIngValues] = useState([]);
  const [selectedIngredient, setSelectedIngredient] = useState<IngredientConflictDto>();
  const [conflictResolutionType, setConflictResolutionType] =
    useState<ConflictResolutionType>(ConflictResolutionType.UseNew);
  const [conflictIngSelectedId, setConflictIngSelectedId] = useState<number>(0);
  const [selectedTypes, setSelectedTypes] = useState<KeyValuePairDTO[]>([]);
  const [selectedAliases, setSelectedAliases] = useState<KeyValuePairDTO[]>([]);
  const [selectedCasNumbers, setSelectedCasNumbers] = useState<KeyValuePairDTO[]>([]);
  const [selectedBufferData, setSelectedBufferData] = useState<KeyValuePairDTO[]>([]);
  const [selectedMatchedIngredient, setSelectedMatchedIngredient] =
    useState<ExtendedIngredientDTO>();

  const [statusMessage, setStatusMessage] = useState<string>('Conflict Resolved.');
  const [isAddMissingStock, setIsAddMissingStock] = useState<boolean>(false);
  const [isUseVendorStock, setIsUseVendorStock] = useState<boolean>(false);
  const [vendorStock, setVendorStock] = useState<string>('Select Stock');
  const [selectedUseStockIds, setSelectedUseStockIds] = useState<number[]>([]);
  const [selectedNewStockIds, setSelectedNewStockIds] = useState<number[]>([]);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const validateIngredientConflict = async (
    modifiedIngredient: ExtendedIngredientDTO,
    conflictIngResolutionType: ConflictResolutionType,
    newIngredient?: IngredientConflictDto,
  ) => {
    let msg = '';
    if (!newIngredient) {
      newIngredient = selectedIngredient;
    }
    if (newIngredient) {
      const result = await ValidateIngredient(
        modifiedIngredient,
        conflictIngResolutionType,
      );
      if (result?.value === null) {
        if (msg !== statusMessage) {
          newIngredient.errorMessage = '';
          newIngredient.hasErrors = false;
          setStatusMessage('Conflict Resolved');
        }
        newIngredient.resolvedIngredient = modifiedIngredient;
      } else {
        msg = result.value;
        if (result.value !== statusMessage) {
          newIngredient.errorMessage = result.value;
          newIngredient.hasErrors = true;
          setStatusMessage(result.value);
        }
        newIngredient.resolvedIngredient = null;
      }

      newIngredient.resolutionType = conflictIngResolutionType;
      let conflictedIngList = cloneDeep(conflictIngredientsList);
      conflictedIngList = conflictIngredientsList.map((item) => {
        if (item.temporaryId == newIngredient?.temporaryId) {
          item = cloneDeep(newIngredient);
        }
        return item;
      });

      if (
        conflictIngResolutionType === ConflictResolutionType.UseSubstitute &&
        updateStocksInMatchedIng &&
        conditions &&
        msg === ''
      ) {
        updateStocksInMatchedIng(conflictIngResolutionType, conflictedIngList, msg);
      } else {
        setConflictIngredientsList(conflictedIngList);
      }
    }
  };

  useEffect(() => {
    if (conflictIngredientsList?.length > 0 && selectedIngredient) {
      ingredientConditions();
      if (conflictResolutionType === ConflictResolutionType.UseNew) {
        validateIngredientConflict(
          selectedIngredient.xmlImportIngredient,
          conflictResolutionType,
        );
      } else {
        validateIngredientConflict(selectedMatchedIngredient, conflictResolutionType);
      }
    }
  }, [selectedIngredient, selectedMatchedIngredient]);

  useEffect(() => {
    setupTable();
  }, [conflictIngredientsList]);

  useEffect(() => {
    if (conflictIngredientsList?.length > 0) {
      setSelectedIngredient(conflictIngredientsList[conflictIngSelectedId]);
    }
  }, [showModal]);

  const getConditionsTableColumns: {
    accessor: string;
    Header: string;
    Filter?: (event: any) => JSX.Element;
    className?: string;
  }[] = [
    {
      accessor: 'No',
      Header: 'No #',
      Filter: handleTableFilter(true, 'No'),
    },
    {
      accessor: 'Type',
      Header: 'Type',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Type'),
    },
    {
      accessor: 'Name',
      Header: 'Name',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Name'),
    },
    {
      accessor: 'StockConc',
      Header: 'Stock Conc.',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Stock Conc'),
    },
    {
      accessor: 'StockLowPH',
      Header: 'Stock Low PH',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Stock Low PH'),
    },
    {
      accessor: 'StockHighPH',
      Header: 'Stock High PH',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Stock High PH'),
    },
    {
      accessor: 'Conc',
      Header: 'Conc',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Conc'),
    },
    {
      accessor: 'Units',
      Header: 'Units',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'Units'),
    },
    {
      accessor: 'pH',
      Header: 'pH',
      className: 'ingredient-list-status',
      Filter: handleTableFilter(true, 'pH'),
    },
  ];

  const setupTable = () => {
    if (conflictIngredientsList) {
      const ingredients = [...conflictIngredientsList];
      const tableData = ingredients.map((ingredient) => {
        const entry = {
          name: (
            <p
              id="ingredientconflictsmodel-setuptable-table-name"
              className="d-flex justify-content-between w-100 text-left pt-3"
            >
              {ingredient.xmlImportIngredient.longName}
            </p>
          ),
          status: (
            <div
              id="ingredientconflictsmodel-setuptable-status"
              className="ingredient-list-status"
              title={ingredient?.errorMessage}
            >
              <RMSvgIcon
                id="ingredientconflictsmodel-setuptable-svg-setup-icon"
                name={ingredient.hasErrors ? 'ExclamationTriangle' : 'Success'}
                width={'26px'}
                height={'26px'}
                fill="#474747"
                subfolder="Setup"
                onClick={() => {}}
              ></RMSvgIcon>
            </div>
          ),
        };
        return entry;
      });
      setValues(tableData);
    }
  };
  const ingredientConditions = () => {
    if (selectedIngredient && conflictIngredientsList) {
      const dataArray = [];
      const conditions = selectedIngredient.xmlImportIngredient.conditionIngredients;
      conditions?.map((condition) => {
        dataArray.push({
          No: condition.conditionNumber,
          Type: condition.type,
          Name: condition.ingredientName,
          StockConc: condition.stockConcentration,
          StockLowPH: condition.pH,
          StockHighPH: condition.highConcentration,
          Conc: condition.concentrationM,
          Units: condition.stockConcentrationUnit,
          pH: condition.pH,
        });
      });
      setConditionIngValues(dataArray);
    }
  };
  const onClickAddTypes = (conflictResolutionType: ConflictResolutionType) => {
    const tempSelectedIngredient = selectedIngredient;
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueTypes = selectedTypes
        .map((type) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.types.filter(
            (selected) =>
              selected.name !== type.name && conflictResolutionType !== type.value,
          );
          if (
            tempIng.length > 0 &&
            tempSelectedIngredient?.xmlImportIngredient?.types.some(
              (q) => q.name === type.name,
            ) === false
          ) {
            const returnObj = new IngredientTypeDTO();
            returnObj.name = type.name;
            returnObj.isBuffer = false;
            returnObj.ingredientTypeColorId = null;
            returnObj.id = type.id;
            return returnObj;
          }
        })
        .filter((ing) => ing !== undefined);

      const appendedType = tempSelectedIngredient?.xmlImportIngredient?.types.concat(
        cloneDeep(uniqueTypes.filter((uniqueTypes) => uniqueTypes !== undefined)),
      );
      tempSelectedIngredient.xmlImportIngredient.types = appendedType;
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueType = selectedTypes
        .map((type) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.types.filter(
            (selected) =>
              selected.name === type.name && conflictResolutionType !== type.value,
          );
          if (tempIng.length > 0) {
            if (matchedIng?.types.some((q) => q.name === type.name) === false) {
              matchedIng.bufferCalculationTypeId =
                tempSelectedIngredient.xmlImportIngredient.bufferCalculationTypeId;
              matchedIng.pKa = tempSelectedIngredient.xmlImportIngredient.pKa;
              matchedIng.bufferData =
                tempSelectedIngredient.xmlImportIngredient.bufferData;
              matchedIng.bufferDataPoint =
                tempSelectedIngredient.xmlImportIngredient.bufferDataPoint;

              const xmlType = tempSelectedIngredient.xmlImportIngredient.types.find(
                (xmlType) => xmlType.name == type.name,
              );
              const returnObj = new IngredientTypeDTO();
              returnObj.name = type.name;
              returnObj.isBuffer = xmlType.isBuffer;
              returnObj.ingredientTypeColorId = null;
              returnObj.id = type.id;
              return returnObj;
            }
          }
        })
        .filter((type) => type !== undefined);

      const appended = matchedIng?.types.concat(cloneDeep(uniqueType));
      matchedIng.types = appended;
      tempSelectedIngredient.matchingIngredients =
        tempSelectedIngredient.matchingIngredients.map((ing) => {
          if (ing.id === matchedIng.id) {
            ing = matchedIng;
          }
          return ing;
        });
    }
    updateIngredientsDetails(tempSelectedIngredient);
  };
  const onClickAddAliases = (conflictResolutionType: ConflictResolutionType) => {
    const tempSelectedIngredient = selectedIngredient;
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueAliases = selectedAliases
        .map((alias) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.aliases.filter(
            (selected) =>
              selected.name !== alias.name && conflictResolutionType !== alias.value,
          );
          if (
            tempIng.length > 0 &&
            tempSelectedIngredient?.xmlImportIngredient?.aliases.some(
              (q) => q.name === alias.name,
            ) === false
          ) {
            const returnObj = new IngredientAlias();
            returnObj.name = alias.name;
            returnObj.id = alias.id;
            returnObj.ingredientId = 0;
            return returnObj;
          }
        })
        .filter((ing) => ing !== undefined);

      const appendedAlias = tempSelectedIngredient?.xmlImportIngredient?.aliases.concat(
        cloneDeep(uniqueAliases.filter((uniqueAliases) => uniqueAliases !== undefined)),
      );
      tempSelectedIngredient.xmlImportIngredient.aliases = appendedAlias;
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueAlias = selectedAliases
        .map((alias) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.aliases.filter(
            (selected) =>
              selected.name === alias.name && conflictResolutionType !== alias.value,
          );
          if (tempIng.length > 0) {
            if (matchedIng?.aliases.some((q) => q.name === alias.name) === false) {
              const returnObj = new IngredientAlias();
              returnObj.name = alias.name;
              returnObj.id = alias.id;

              return returnObj;
            }
          }
        })
        .filter((alias) => alias !== undefined);

      const appended = matchedIng?.aliases.concat(cloneDeep(uniqueAlias));
      matchedIng.aliases = appended;
      tempSelectedIngredient.matchingIngredients =
        tempSelectedIngredient.matchingIngredients.map((ing) => {
          if (ing.id === matchedIng.id) {
            ing = matchedIng;
          }
          return ing;
        });
    }
    updateIngredientsDetails(tempSelectedIngredient);
  };
  const onClickAddCasNumbers = (conflictResolutionType: ConflictResolutionType) => {
    const tempSelectedIngredient = selectedIngredient;
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueCasNumbers = selectedCasNumbers
        .map((casNumber) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.casnumbers.filter(
            (selected) =>
              selected.number !== casNumber.name &&
              conflictResolutionType !== casNumber.value,
          );
          if (
            tempIng.length > 0 &&
            tempSelectedIngredient?.xmlImportIngredient?.casnumbers.some(
              (q) => q.number === casNumber.name,
            ) === false
          ) {
            const returnObj = new CasNumberDTO();
            returnObj.number = casNumber.name;
            return returnObj;
          }
        })
        .filter((ing) => ing !== undefined);

      const appendedCasNumber =
        tempSelectedIngredient?.xmlImportIngredient?.casnumbers.concat(
          cloneDeep(
            uniqueCasNumbers.filter((uniqueCasNumbers) => uniqueCasNumbers !== undefined),
          ),
        );
      tempSelectedIngredient.xmlImportIngredient.casnumbers = appendedCasNumber;
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueAlias = selectedCasNumbers
        .map((casNumber) => {
          const tempIng = tempSelectedIngredient?.xmlImportIngredient?.casnumbers.filter(
            (selected) =>
              selected.number === casNumber.name &&
              conflictResolutionType !== casNumber.value,
          );
          if (tempIng.length > 0) {
            if (
              matchedIng?.casnumbers.some((q) => q.number === casNumber.name) === false
            ) {
              const returnObj = new CasNumberDTO();
              returnObj.number = casNumber.name;
              return returnObj;
            }
          }
        })
        .filter((casNumber) => casNumber !== undefined);

      const appended = matchedIng?.casnumbers.concat(cloneDeep(uniqueAlias));
      matchedIng.casnumbers = appended;
      tempSelectedIngredient.matchingIngredients =
        tempSelectedIngredient.matchingIngredients.map((ing) => {
          if (ing.id === matchedIng.id) {
            ing = matchedIng;
          }
          return ing;
        });
    }
    updateIngredientsDetails(tempSelectedIngredient);
  };
  const onClickAddBufferData = (conflictResolutionType: ConflictResolutionType) => {
    const tempSelectedIngredient = selectedIngredient;

    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const matchedIng = selectedMatchedIngredient;
      tempSelectedIngredient.xmlImportIngredient.bufferDataPoint =
        matchedIng?.bufferDataPoint;
    } else {
      const matchedIngr = selectedMatchedIngredient;
      if (matchedIngr.bufferCalculationTypeId === null) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Buffer data could not be copied to this ingredient because the ingredient is not currently listed as a buffer. If you want to add a buffer to this ingredient, you must add the buffer type to the ingredient, and then try again',
        );
        return;
      }
      matchedIngr.bufferDataPoint = cloneDeep(
        tempSelectedIngredient?.xmlImportIngredient?.bufferDataPoint,
      );
      tempSelectedIngredient.matchingIngredients =
        tempSelectedIngredient.matchingIngredients.map((ing) => {
          if (ing.id === matchedIngr.id) {
            ing = cloneDeep(matchedIngr);
          }
          return ing;
        });
    }
    updateIngredientsDetails(tempSelectedIngredient);
  };
  const onClickDeleteTypes = (conflictResolutionType: ConflictResolutionType) => {
    let allowDelete = true;
    const tempSelectedIngredient = selectedIngredient;
    if (
      selectedTypes.some((selected) => conflictResolutionType === selected.value) ===
      false
    ) {
      return;
    }
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueTypes = tempSelectedIngredient?.xmlImportIngredient?.types.filter(
        (type) => {
          const index = selectedTypes.findIndex(
            (selected) =>
              selected.name === type.name && conflictResolutionType === selected.value,
          );
          if (index === -1) {
            return type;
          }
          allowDelete = false;
        },
      );
      if (allowDelete) {
        tempSelectedIngredient.xmlImportIngredient.types = uniqueTypes;
      }
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueType = matchedIng?.types
        .map((type) => {
          const index = selectedTypes.findIndex(
            (selected) =>
              selected.name === type.name && conflictResolutionType === selected.value,
          );
          if (index === -1) {
            return type;
          } else if (type.id > 0) {
            allowDelete = false;
          }
        })
        .filter((type) => type !== undefined);
      if (allowDelete) {
        matchedIng.types = uniqueType;
        tempSelectedIngredient.matchingIngredients =
          tempSelectedIngredient.matchingIngredients.map((ing) => {
            if (ing.id === matchedIng.id) {
              ing = matchedIng;
            }
            return ing;
          });
      }
    }
    if (allowDelete) {
      updateIngredientsDetails(tempSelectedIngredient);
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You cannot remove existing types.',
      );
    }
  };
  const onClickDeleteAliases = (conflictResolutionType: ConflictResolutionType) => {
    let allowDelete = true;
    const tempSelectedIngredient = selectedIngredient;
    if (
      selectedAliases.some((selected) => conflictResolutionType === selected.value) ===
      false
    ) {
      return;
    }
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueAliases = tempSelectedIngredient?.xmlImportIngredient?.aliases.filter(
        (alias) => {
          const tempAlias = selectedAliases.find(
            (selected) =>
              selected.name === alias.name && conflictResolutionType === selected.value,
          );
          if (!tempAlias) {
            return alias;
          } else if (tempAlias?.id === 0) {
            allowDelete = false;
          }
        },
      );
      if (allowDelete) {
        tempSelectedIngredient.xmlImportIngredient.aliases = uniqueAliases;
      }
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueAlias = matchedIng?.aliases
        .map((alias) => {
          const tempalias = selectedAliases.find(
            (selected) =>
              selected.name === alias.name && conflictResolutionType === selected.value,
          );
          if (!tempalias) {
            return alias;
          } else if (tempalias?.id > 0) {
            allowDelete = false;
          }
        })
        .filter((alias) => alias !== undefined);
      if (allowDelete) {
        matchedIng.aliases = uniqueAlias;
        tempSelectedIngredient.matchingIngredients =
          tempSelectedIngredient.matchingIngredients.map((ing) => {
            if (ing.id === matchedIng.id) {
              ing = matchedIng;
            }
            return ing;
          });
      }
    }
    if (allowDelete) {
      updateIngredientsDetails(tempSelectedIngredient);
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You cannot remove existing aliases.',
      );
    }
  };
  const onClickDeleteCasNumbers = (conflictResolutionType: ConflictResolutionType) => {
    let allowDelete = true;
    const tempSelectedIngredient = selectedIngredient;
    if (
      selectedCasNumbers.some((selected) => conflictResolutionType === selected.value) ===
      false
    ) {
      return;
    }
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      const uniqueCasNumbers =
        tempSelectedIngredient?.xmlImportIngredient?.casnumbers.filter((casNumber) => {
          const tempCasnumber = selectedCasNumbers.find(
            (selected) =>
              selected.name === casNumber.number &&
              conflictResolutionType === selected.value,
          );
          if (!tempCasnumber) {
            return casNumber;
          } else if (tempCasnumber?.id < 0) {
            allowDelete = false;
          }
        });
      if (allowDelete) {
        tempSelectedIngredient.xmlImportIngredient.casnumbers = uniqueCasNumbers;
      }
    } else {
      const matchedIng = selectedMatchedIngredient;
      const uniqueCasNumber = matchedIng?.casnumbers
        .map((casNumber) => {
          const tempCasnumbers = selectedCasNumbers.find(
            (selected) =>
              selected.name === casNumber.number &&
              conflictResolutionType === selected.value,
          );
          if (!tempCasnumbers) {
            return casNumber;
          } else if (tempCasnumbers?.id > 0) {
            allowDelete = false;
          }
        })
        .filter((casNumber) => casNumber !== undefined);
      if (allowDelete) {
        matchedIng.casnumbers = uniqueCasNumber;
        tempSelectedIngredient.matchingIngredients =
          tempSelectedIngredient.matchingIngredients.map((ing) => {
            if (ing.id === matchedIng.id) {
              ing = matchedIng;
            }
            return ing;
          });
      }
    }
    if (allowDelete) {
      updateIngredientsDetails(tempSelectedIngredient);
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You cannot remove existing casNumbers.',
      );
    }
  };

  const updateIngredientsDetails = (tempSelectedIngredient) => {
    conflictIngredientsList.map((conflicted) => {
      if (conflicted.temporaryId === tempSelectedIngredient.temporaryId) {
        conflicted = cloneDeep(tempSelectedIngredient);
      }
    });
    let resolvedIngredients: IngredientConflictDto[];
    const isExist = conflictIngredientsList.some(
      (conflicted) => conflicted.temporaryId === tempSelectedIngredient.temporaryId,
    );
    if (isExist === false) {
      resolvedIngredients = resolvedIngredientsList.concat(tempSelectedIngredient);
    } else {
      resolvedIngredients = resolvedIngredientsList.map((ing) => {
        if (ing.temporaryId === tempSelectedIngredient.temporaryId) {
          ing = tempSelectedIngredient;
        }
        return ing;
      });
    }
    setResolvedIngredientsList(resolvedIngredients);
    setConflictIngredientsList(conflictIngredientsList);
    setSelectedIngredient(cloneDeep(tempSelectedIngredient));
    setSelectedTypes(
      selectedTypes.filter((type) => type.value !== conflictResolutionType),
    );
  };
  const handleSelectionChange = (value: any[]) => {
    if (value.length > 0 && value[value.length - 1] !== conflictIngSelectedId) {
      setSelectedTypes([]);
      setSelectedAliases([]);
      setSelectedBufferData([]);
      setSelectedCasNumbers([]);
      setSelectedNewStockIds([]);
      setSelectedUseStockIds([]);

      const rowNumber = value[value.length - 1];
      conflictIngredientsList[rowNumber]?.resolutionType
        ? setConflictResolutionType(conflictIngredientsList[rowNumber]?.resolutionType)
        : setConflictResolutionType(ConflictResolutionType.UseNew);
      setSelectedIngredient(conflictIngredientsList[rowNumber]);
      setSelectedMatchedIngredient(
        conflictIngredientsList[rowNumber].matchingIngredients[0],
      );
      conflictIngredientsList.map((conflicted) => {
        if (conflicted.temporaryId === conflictIngredientsList[rowNumber].temporaryId) {
          conflicted = cloneDeep(conflictIngredientsList[rowNumber]);
        }
      });
      setConflictIngSelectedId(rowNumber);
      setConflictIngredientsList(conflictIngredientsList);
    }
  };
  const conditionsTable = () => {
    return (
      <div
        id="ingredientconflictsmodel-conditionstable-wrapper"
        className="border-box w-100 conditions-parent"
      >
        <RMTable columns={getConditionsTableColumns} data={conditionIngValues}></RMTable>
      </div>
    );
  };

  const onAddRestAsNewClick = () => {
    const tempConflictIngredientsList = conflictIngredientsList;
    const updatedConflictIngredientsList = tempConflictIngredientsList.map((ing) => {
      if (ing.resolutionType === ConflictResolutionType.None) {
        ing.resolutionType = ConflictResolutionType.UseNew;
      }
      if (
        ing.resolutionType === ConflictResolutionType.UseNew &&
        ing.conflictType !== IngredientImportConflictType.ExactMatch
      ) {
        validateIngredientConflict(ing.xmlImportIngredient, ing.resolutionType, ing);
        ing.resolvedIngredient = ing.xmlImportIngredient;
      }
      return ing;
    });
    setConflictIngredientsList(updatedConflictIngredientsList);
  };

  const onChangeIngredientType = (
    value: ConflictResolutionType,
    _matchedIng?: ExtendedIngredientDTO,
  ) => {
    if (!selectedIngredient) {
      return;
    }
    const matchedIng = _matchedIng || selectedMatchedIngredient;

    selectedIngredient.resolutionType = value;
    setConflictResolutionType(value);
    let tempConditions = conditions;
    if (value === ConflictResolutionType.UseNew) {
      const ingingTypes =
        selectedIngredient.xmlImportIngredient.ingredientIngredientType.map(
          (ingIngType) => {
            const tempingingType = matchedIng.ingredientIngredientType.find(
              (iningType) =>
                iningType.ingredientId === matchedIng.id &&
                iningType.ingredientTypeId === ingIngType.ingredientTypeId,
            );
            if (tempingingType && tempConditions) {
              tempConditions = tempConditions.map((condition) => {
                const tempConditionIngs = condition.conditionIngredient.map((condIng) => {
                  if (condIng.ingredientIngredientTypeId === tempingingType.id) {
                    condIng.ingredientIngredientTypeId = ingIngType.id;
                  }
                  return condIng;
                });
                condition.conditionIngredient = tempConditionIngs;
                return condition;
              });
              ingIngType = tempingingType;
            } else {
              ingIngType.ingredientId = matchedIng.id;
            }
            return ingIngType;
          },
        );
      const modifiedIngredient = {
        ...selectedIngredient.xmlImportIngredient,
      } as ExtendedIngredientDTO;
      modifiedIngredient.ingredientIngredientType = ingingTypes;
      validateIngredientConflict(modifiedIngredient, value);
    } else {
      let selectedMatchedIng: ExtendedIngredientDTO;
      if (selectedIngredient.matchingIngredients.length > 0 && !matchedIng) {
        selectedMatchedIng = selectedIngredient.matchingIngredients[0];
      } else if (matchedIng) {
        selectedMatchedIng = matchedIng;
      }

      const ingingTypes =
        selectedIngredient.xmlImportIngredient.ingredientIngredientType.map(
          (ingIngType) => {
            const tempingingType = matchedIng.ingredientIngredientType.find(
              (iningType) =>
                iningType.ingredientId === selectedMatchedIng.id &&
                iningType.ingredientTypeId === ingIngType.ingredientTypeId,
            );
            if (tempingingType && tempConditions) {
              tempConditions = tempConditions.map((condition) => {
                const tempConditionIngs = condition.conditionIngredient.map((condIng) => {
                  if (condIng.ingredientIngredientTypeId === ingIngType.id) {
                    condIng.ingredientIngredientTypeId = tempingingType.id;
                  }
                  return condIng;
                });
                condition.conditionIngredient = tempConditionIngs;
                return condition;
              });
              ingIngType = tempingingType;
            } else {
              ingIngType.ingredientId = selectedMatchedIng.id;
              if (
                isScreen &&
                !matchedIng.types.some((type) => type.id === ingIngType.ingredientTypeId)
              ) {
                const ingType = selectedIngredient.xmlImportIngredient.types.find(
                  (type) => type.id === ingIngType.ingredientTypeId,
                );
                matchedIng.types.push(ingType);
                if (
                  ingType.isBuffer &&
                  matchedIng.defaultBufferIngredientStockId === null
                ) {
                  const stockIndex =
                    selectedIngredient.xmlImportIngredient.ingredientStock.findIndex(
                      (stock) =>
                        stock.id ===
                        selectedIngredient.xmlImportIngredient
                          .defaultBufferIngredientStockId,
                    );
                  if (stockIndex >= 0) {
                    const stock =
                      selectedIngredient.xmlImportIngredient.ingredientStock[stockIndex];
                    matchedIng.ingredientStock.push(stock);
                    selectedMatchedIng.defaultBufferIngredientStockId = stock.id;
                    selectedMatchedIng.bufferCalculationTypeId =
                      selectedIngredient.xmlImportIngredient.bufferCalculationTypeId;
                    selectedMatchedIng.bufferData =
                      selectedIngredient.xmlImportIngredient.bufferData;
                    selectedMatchedIng.pKa = selectedIngredient.xmlImportIngredient.pKa;
                    setSelectedNewStockIds([...selectedNewStockIds, stockIndex]);
                  }
                }
              }
            }
            return ingIngType;
          },
        );

      selectedMatchedIng.ingredientIngredientType = [
        ...selectedMatchedIng.ingredientIngredientType,
        ...ingingTypes,
      ];

      selectedIngredient.xmlImportIngredient.ingredientStock.map((xmlIngStock) => {
        const stock = findMatchingStockAvailable(selectedMatchedIng, xmlIngStock);
        if (stock && tempConditions) {
          tempConditions = tempConditions.map((condition) => {
            const tempConditionIngs = condition.conditionIngredient.map((condIng) => {
              if (condIng.ingredientStockId === xmlIngStock.id) {
                condIng.ingredientStockId = stock.id;
              }
              if (condIng.highPhingredientStockId === xmlIngStock.id) {
                condIng.highPhingredientStockId = stock.id;
              }
              return condIng;
            });
            condition.conditionIngredient = tempConditionIngs;
            return condition;
          });
        }
      });
      addIngredientNameAsAlias(
        selectedMatchedIng,
        selectedIngredient?.xmlImportIngredient.longName,
      );
      setSelectedMatchedIngredient(selectedMatchedIng);
      setConflictIngredientsList(cloneDeep(conflictIngredientsList));
    }
    setConditions && setConditions(tempConditions);
    setSelectedIngredient(selectedIngredient);
    updateIngredientsDetails(selectedIngredient);
  };
  const findMatchingStockAvailable = (selectedMatchedIng, stock) => {
    return selectedMatchedIng?.ingredientStock.find(
      (ingstock) =>
        ingstock.stockConcentration === stock.stockConcentration &&
        ingstock.concentrationUnits === stock.concentrationUnits &&
        ingstock.stockPh === stock.stockPh &&
        ingstock.vendorName === stock.vendorName &&
        ingstock.vendorPartNumber === stock.vendorPartNumber,
    );
  };
  const updateStocksInMatchedIng = (
    conflictResolutionType,
    conflictedIngList,
    statusMsg,
  ) => {
    if (conflictResolutionType === ConflictResolutionType.UseSubstitute) {
      const newStockIds = [...selectedNewStockIds];
      const useStockIds = [...selectedUseStockIds];

      selectedIngredient.xmlImportIngredient.ingredientStock.map((stock, index) => {
        let isSimilarStockFound = false;
        let isVendorStockMissing = false;
        let isMissingStockResolved = false;

        const matchedStocks = selectedMatchedIngredient.ingredientStock.filter(
          (ingstock) =>
            ingstock.stockConcentration === stock.stockConcentration &&
            ingstock.concentrationUnits === stock.concentrationUnits &&
            ingstock.stockPh === stock.stockPh,
        );
        if (isUseVendorStock) {
          const matchedStock = matchedStocks.find(
            (item) =>
              item.vendorName === vendorStock &&
              item.vendorPartNumber === stock.vendorPartNumber,
          );
          if (matchedStock) {
            isSimilarStockFound = true;
            stock.ingredientId = matchedStock.ingredientId;
            const vendorStockIndex = selectedMatchedIngredient.ingredientStock.findIndex(
              (item) => item == matchedStock,
            );
            if (vendorStockIndex >= 0) {
              if (!useStockIds.some((ind) => ind === vendorStockIndex)) {
                useStockIds.push(vendorStockIndex);
              }
              if (!newStockIds.some((ind) => ind === index)) {
                newStockIds.push(index);
              }
              isMissingStockResolved = true;
            }
          } else if (matchedStocks.length > 0) {
            const matchedStock = matchedStocks[0];
            stock.ingredientId = matchedStock.ingredientId;
            const stockIndex = selectedMatchedIngredient.ingredientStock.findIndex(
              (item) => item == matchedStock,
            );
            if (stockIndex >= 0 && !useStockIds.some((ind) => ind === index)) {
              useStockIds.push(stockIndex);
              newStockIds.push(index);
              isMissingStockResolved = true;
            }
          } else {
            isMissingStockResolved = false;
            isVendorStockMissing = true;
          }
        } else {
          const matchedStock = matchedStocks.find(
            (item) =>
              item.vendorName === stock.vendorName &&
              item.vendorPartNumber === stock.vendorPartNumber,
          );
          if (matchedStock) {
            stock.ingredientId = selectedMatchedIngredient.id;
            isMissingStockResolved = true;
          }
        }

        if ((!isSimilarStockFound && isAddMissingStock) || isVendorStockMissing) {
          const matchedStocks = selectedMatchedIngredient.ingredientStock.filter(
            (ingstock) => {
              if (
                ingstock.stockConcentration === stock.stockConcentration &&
                ingstock.concentrationUnits === stock.concentrationUnits &&
                ingstock.stockPh === stock.stockPh &&
                ingstock.vendorName === stock.vendorName &&
                ingstock.vendorPartNumber === stock.vendorPartNumber
              ) {
                stock.ingredientId = ingstock.ingredientId;
                return ingstock;
              }
            },
          );
          if (matchedStocks.length > 0) {
            isSimilarStockFound = true;
          }
          stock.ingredientId = selectedMatchedIngredient.id;
          if (
            !isSimilarStockFound &&
            !selectedMatchedIngredient.ingredientStock.some((ingStock) =>
              isEqual(ingStock, stock),
            )
          ) {
            selectedMatchedIngredient.ingredientStock.push(stock);
            useStockIds.push(selectedMatchedIngredient.ingredientStock.length - 1);
          } else if (isSimilarStockFound) {
            const index = selectedMatchedIngredient.ingredientStock.findIndex(
              (ingStock) => isEqual(ingStock, matchedStocks[0]),
            );
            useStockIds.push(index);
            isMissingStockResolved = true;
          }
          if (!newStockIds.some((ind) => ind === index)) {
            newStockIds.push(index);
            isMissingStockResolved = true;
          }
        }

        if (!isMissingStockResolved) {
          statusMsg = 'Missing stock(s) are not available in Subtitute ingredient stocks';
        }
      });

      setSelectedNewStockIds(newStockIds);
      setSelectedUseStockIds(useStockIds);
      if (statusMsg !== '') {
        selectedIngredient.errorMessage = statusMsg;
        selectedIngredient.resolvedIngredient = null;
        selectedIngredient.hasErrors = true;
      } else {
        statusMsg = 'Conflict Resolved';
        selectedIngredient.resolvedIngredient = selectedMatchedIngredient;
        selectedIngredient.errorMessage = '';
        selectedIngredient.hasErrors = false;
      }
      setStatusMessage(statusMsg);
      const matchedIngs = selectedIngredient?.matchingIngredients.map((ing) => {
        if (ing.id === selectedMatchedIngredient.id) {
          ing = cloneDeep(selectedMatchedIngredient);
        }
        return ing;
      });
      selectedIngredient.matchingIngredients = cloneDeep(matchedIngs);
      const tempConflictIngredientsList = cloneDeep(conflictedIngList);
      tempConflictIngredientsList[conflictIngSelectedId] = selectedIngredient;
      setConflictIngredientsList(cloneDeep(tempConflictIngredientsList));
      setSelectedMatchedIngredient(selectedMatchedIngredient);
      setSelectedIngredient(selectedIngredient);
    }
  };
  const addIngredientNameAsAlias = (
    matchedIngredient: ExtendedIngredientDTO,
    ingredientName: string,
  ) => {
    const tmpIngredientName = ingredientName.toLowerCase();
    if (
      tmpIngredientName === matchedIngredient.shortName.toLowerCase() ||
      tmpIngredientName === matchedIngredient.longName.toLowerCase()
    ) {
      return;
    }

    if (
      tmpIngredientName === matchedIngredient.longName.toLowerCase() ||
      tmpIngredientName === matchedIngredient.shortName.toLowerCase()
    ) {
      return;
    }

    if (
      matchedIngredient.aliases?.some(
        (alias) => alias.name.toLowerCase() === tmpIngredientName,
      ) === true
    ) {
      return;
    }
    const ingredientAlias: IngredientAlias = new IngredientAlias();
    ingredientAlias.id = getNewId();
    ingredientAlias.ingredientId = matchedIngredient.id;
    ingredientAlias.name = ingredientName;
    matchedIngredient.aliases.push(ingredientAlias);
  };

  const onCancel = () => {
    setShowModal(false);
  };

  const setSelectedIngredientByNewIngredient = (ingredient) => {
    const tempSelectedIngredient = selectedIngredient;
    tempSelectedIngredient.xmlImportIngredient = ingredient;
    setSelectedIngredient(cloneDeep(tempSelectedIngredient));
    const tempConflictIngredientsList = conflictIngredientsList.map((ing) => {
      if (ing.temporaryId == ingredient.temporaryId) {
        ing = ingredient;
      }
      return ing;
    });
    setConflictIngredientsList(tempConflictIngredientsList);
  };

  const setSelectedIngredientBySubsitudeIngredient = (ingredient) => {
    const tempSelectedIngredient = selectedIngredient;
    tempSelectedIngredient.matchingIngredients =
      tempSelectedIngredient.matchingIngredients.map((ing) => {
        if (ing.id === ingredient.id) {
          ing = ingredient;
        }
        return ing;
      });
    const tempConflictIngredientsList = conflictIngredientsList.map((ing) => {
      if (ing.temporaryId == ingredient.temporaryId) {
        ing = ingredient;
      }
      return ing;
    });
    setConflictIngredientsList(tempConflictIngredientsList);
    setSelectedIngredient(cloneDeep(tempSelectedIngredient));
  };
  const AddNewIngredient = () => {
    const xmlImportIngredient = selectedIngredient?.xmlImportIngredient;
    return (
      <newIngredientContext.Provider
        value={{
          selectedIngredient: xmlImportIngredient,
          setSelectedIngredient: setSelectedIngredientByNewIngredient,
          selectedTypes: selectedTypes,
          setSelectedTypes: setSelectedTypes,
          selectedAliases: selectedAliases,
          setSelectedAliases: setSelectedAliases,
          selectedCasNumbers: selectedCasNumbers,
          setSelectedCasNumbers: setSelectedCasNumbers,
          selectedBufferData: selectedBufferData,
          setSelectedBufferData: setSelectedBufferData,
          onClickAddTypes: onClickAddTypes,
          onClickAddAliases: onClickAddAliases,
          onClickAddCasNumbers: onClickAddCasNumbers,
          onClickAddBufferData: onClickAddBufferData,
          onClickDeleteTypes: onClickDeleteTypes,
          onClickDeleteAliases: onClickDeleteAliases,
          onClickDeleteCasNumbers: onClickDeleteCasNumbers,
          selectedIds: selectedNewStockIds,
          setSelectedIds: onClickSelectedNewStockIds,
          conflictResolutionType: conflictResolutionType,
        }}
      >
        <NewIngredient
          vendors={vendors}
          setVendorStock={setVendorStock}
          setIsUseVendorStock={setIsUseVendorStock}
          setIsAddMissingStock={setIsAddMissingStock}
          isAddMissingStock={isAddMissingStock}
          isUseVendorStock={isUseVendorStock}
          onChangeIngredientType={onChangeIngredientType}
          vendorStock={vendorStock}
        />
      </newIngredientContext.Provider>
    );
  };
  const ingredientsConditions = () => {
    let conditionsTableDiv = <></>;
    if (isScreen === true) {
      conditionsTableDiv = (
        <div
          id="ingredientconflictsmodel-ingredientsconditions-wrapper"
          className="d-inline-block"
        >
          <h6 id="ingredientconflictsmodel-ingredientsconditions-label">
            Ingredients Conditions in use
          </h6>
          <>{conditionsTable()}</>
        </div>
      );
    }
    return <>{conditionsTableDiv}</>;
  };
  const onClickSelectedNewStockIds = (newStockIds: number[]) => {
    if (conflictResolutionType === ConflictResolutionType.UseNew) {
      return;
    }
    let indices = null;
    let isSelected = false;
    if (newStockIds.length > selectedNewStockIds.length) {
      isSelected = true;
      indices = newStockIds.filter((id) => !selectedNewStockIds.includes(id));
    } else {
      indices = selectedNewStockIds.filter((id) => !newStockIds.includes(id));
    }
    let tempUseStockIds = selectedUseStockIds;
    const tempSelectedIngredient = selectedIngredient;
    const tempSelectedMatchedIngredient = selectedMatchedIngredient;
    if (newStockIds.length === 0) {
      const stocks = tempSelectedMatchedIngredient.ingredientStock.filter(
        (ingStock) => ingStock.id > 0,
      );
      tempSelectedMatchedIngredient.ingredientStock = stocks;
      setSelectedMatchedIngredient(tempSelectedMatchedIngredient);
      tempUseStockIds = [];
    } else {
      indices.map((value) => {
        const stock = tempSelectedIngredient.xmlImportIngredient.ingredientStock[value];
        if (
          isSelected &&
          !tempSelectedMatchedIngredient.ingredientStock.find((ingStock) =>
            isEqual(ingStock, stock),
          )
        ) {
          const matchedStocks = tempSelectedMatchedIngredient.ingredientStock.filter(
            (ingstock, i) => {
              if (
                ingstock.stockConcentration === stock.stockConcentration &&
                ingstock.concentrationUnits === stock.concentrationUnits &&
                ingstock.stockPh === stock.stockPh &&
                ingstock.vendorName === stock.vendorName &&
                ingstock.vendorPartNumber === stock.vendorPartNumber
              ) {
                tempUseStockIds.push(i);
                stock.ingredientId = ingstock.ingredientId;
                return ingstock;
              }
            },
          );
          if (matchedStocks.length > 0) {
            setSelectedUseStockIds(tempUseStockIds);
          } else {
            const ingingType = tempSelectedMatchedIngredient.ingredientIngredientType;
            ingingType.map((iit) => {
              if (
                !tempSelectedMatchedIngredient.types.some(
                  (type) => type.id === iit.ingredientTypeId,
                )
              ) {
                const type = selectedIngredient?.xmlImportIngredient.types.find(
                  (type) => type.id === iit.ingredientTypeId,
                );
                tempSelectedMatchedIngredient.types.push(type);
              }
            });

            stock.ingredientId = tempSelectedMatchedIngredient.id;
            tempSelectedMatchedIngredient.ingredientStock.push(stock);
            if (
              stock.isBuffer &&
              !tempSelectedMatchedIngredient.defaultBufferIngredientStockId
            ) {
              // Case : Buffer stock Added to a non buffer Ingredient
              // Copy all buffer data to new ingredient
              tempSelectedMatchedIngredient.defaultBufferIngredientStockId = stock.id;
              tempSelectedMatchedIngredient.bufferCalculationTypeId =
                selectedIngredient.xmlImportIngredient.bufferCalculationTypeId;
              tempSelectedMatchedIngredient.pKa =
                selectedIngredient.xmlImportIngredient.pKa;
              tempSelectedMatchedIngredient.bufferDataPoint =
                selectedIngredient.xmlImportIngredient.bufferDataPoint;
            } else if (
              !stock.isBuffer &&
              !tempSelectedMatchedIngredient.defaultNonBufferIngredientStockId
            ) {
              tempSelectedMatchedIngredient.defaultNonBufferIngredientStockId = stock.id;
            }
            tempUseStockIds.push(
              tempSelectedMatchedIngredient.ingredientStock.length - 1,
            );
          }
        } else if (!isSelected) {
          let usedIndex = tempSelectedMatchedIngredient.ingredientStock.findIndex(
            (ingStock) => isEqual(ingStock, stock),
          );
          if (usedIndex === -1) {
            usedIndex = tempSelectedMatchedIngredient.ingredientStock.findIndex(
              (ingstock) =>
                ingstock.stockConcentration === stock.stockConcentration &&
                ingstock.concentrationUnits === stock.concentrationUnits &&
                ingstock.stockPh === stock.stockPh &&
                ingstock.vendorName === stock.vendorName &&
                ingstock.vendorPartNumber === stock.vendorPartNumber,
            );
          } else {
            const ingStocks = tempSelectedMatchedIngredient.ingredientStock.filter(
              (ingStock, i) => usedIndex !== i,
            );
            tempSelectedMatchedIngredient.ingredientStock = ingStocks;
            setSelectedMatchedIngredient(tempSelectedMatchedIngredient);
          }
          tempUseStockIds = tempUseStockIds.filter((index) => index !== usedIndex);
        }
      });
    }
    setSelectedUseStockIds(tempUseStockIds);
    setSelectedNewStockIds(newStockIds);
    let statusMsg = 'Conflict Resolved';
    if (
      newStockIds.length === selectedIngredient.xmlImportIngredient.ingredientStock.length
    ) {
      selectedIngredient.resolvedIngredient = selectedMatchedIngredient;
      selectedIngredient.errorMessage = '';
      selectedIngredient.hasErrors = false;
    } else {
      statusMsg = 'Missing stock(s) are not available in Subtitute ingredient stocks';
      selectedIngredient.resolvedIngredient = null;
      selectedIngredient.errorMessage = statusMsg;
      selectedIngredient.hasErrors = true;
    }
    setStatusMessage(statusMsg);
    const index = conflictIngredientsList.findIndex(
      (ing) => ing.temporaryId === selectedIngredient.temporaryId,
    );
    conflictIngredientsList[index] = selectedIngredient;
    setConflictIngredientsList(cloneDeep(conflictIngredientsList));
    setSelectedIngredient(cloneDeep(selectedIngredient));
  };

  const importClick = async () => {
    try {
      setIsImporting(true);
      await saveIngredients();
    } finally {
      setIsImporting(false);
    }
  };

  const SubsitudeIngredient = () => {
    return (
      <substituteIngredientContext.Provider
        value={{
          conflictResolutionType: conflictResolutionType,
          setSelectedIngredient: setSelectedIngredientBySubsitudeIngredient,
          onChangeIngredientType: onChangeIngredientType,
          matchedIngredients: selectedIngredient?.matchingIngredients,
          selectedTypes: selectedTypes,
          setSelectedTypes: setSelectedTypes,
          selectedAliases: selectedAliases,
          setSelectedAliases: setSelectedAliases,
          selectedCasNumbers: selectedCasNumbers,
          setSelectedCasNumbers: setSelectedCasNumbers,
          selectedBufferData: selectedBufferData,
          setSelectedBufferData: setSelectedBufferData,
          setSelectedMatchedIngredient: setSelectedMatchedIngredient,
          selectedMatchedIngredient: selectedMatchedIngredient,
          onClickAddTypes: onClickAddTypes,
          onClickAddAliases: onClickAddAliases,
          onClickAddCasNumbers: onClickAddCasNumbers,
          onClickAddBufferData: onClickAddBufferData,
          onClickDeleteTypes: onClickDeleteTypes,
          onClickDeleteAliases: onClickDeleteAliases,
          onClickDeleteCasNumbers: onClickDeleteCasNumbers,
          selectedIds: selectedUseStockIds,
          setSelectedIds: setSelectedUseStockIds,
          conditions,
          setConditions,
          selectedIngredient: selectedIngredient,
        }}
      >
        <SubstituteIngredient
          matchedIngredients={selectedIngredient?.matchingIngredients}
        />
      </substituteIngredientContext.Provider>
    );
  };

  return (
    <FmlxModal
      id="ingredientsconflictmodal-wrapper"
      open={showModal}
      onCloseClick={onCancel}
      title="Import Ingredients Options"
      size={FmlxModal.Size.LARGE}
      hideCloseButton
      secondaryButton={{
        show: true,
        label: 'Import',
        onClick: () => importClick(),
        disabled: isImporting || conflictIngredientsList.some((ing) => ing.hasErrors),
        variant: FmlxButton.Variant.CONTAIN,
        type: FmlxButton.Type.PRIMARY,
      }}
      primaryButton={{
        show: true,
        label: 'Cancel',
        onClick: () => onCancel(),
        disabled: false,
        variant: FmlxButton.Variant.OUTLINE,
        type: FmlxButton.Type.PRIMARY,
      }}
      tertiaryButton={{ show: false } as any}
    >
      <div
        id="ingredientsconflictmodal-body"
        className="ingredients-conflict-modal-body pb-1"
      >
        <ConflictedIngredient
          checkBoxSelectionChange={handleSelectionChange}
          conflictIngSelectedId={conflictIngSelectedId}
          addRestAsNewClick={onAddRestAsNewClick}
          values={values}
          status={statusMessage}
        />
        <div id="ingredientsconflictmodal-options-row" className="row">
          <div
            id="ingredientsconflictmodal-option-addnew"
            className="col-md-6 ingredients-conflict-option "
          >
            {AddNewIngredient()}
          </div>
          <div
            id="ingredientsconflictmodal-option-substitute"
            className="col-md-6 ingredients-conflict-option "
          >
            {SubsitudeIngredient()}
          </div>
          <div id="ingredientsconflictmodal-conditions" className="col-md-12">
            {ingredientsConditions()}
          </div>
        </div>
      </div>
    </FmlxModal>
  );
};

const handleTableFilter = (filterable: boolean, filterTitle: string) => (event: any) =>
  <TableSortInputFilter event={event} title={filterTitle} showSearchInput={filterable} />;

export { IngredientsConflictsModel, newIngredientContext, substituteIngredientContext };
